import { spacing } from "globalStyles";
import React, { ButtonHTMLAttributes, MouseEventHandler, ReactNode } from "react";
import styled from "styled-components";
import Body from "ui/Text/Body";
import { getDynamicDefaultStyle, getDynamicHoverStyle } from "./helper";
import FlexBox from "ui/FlexBox";
import { Theme } from "themes/light";

export type ButtonVariant = "dense" | "outlined" | "link";
export type ButtonColor = keyof Theme["colors"]["button"];

interface Props extends ButtonHTMLAttributes<HTMLButtonElement> {
  color?: ButtonColor;
  variant?: ButtonVariant;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  children: ReactNode;
  className?: string;
  Icon?: ReactNode;
  height?: number;
}

const Button: React.FC<Props> = ({
  color = "primary",
  variant = "dense",
  onClick,
  children,
  className,
  Icon,
  height,
  ...other
}) => {
  return (
    <StyledButton
      className={className}
      color={color}
      onClick={onClick}
      variant={variant}
      height={height}
      {...other}
    >
      <FlexBox alignItems="center">
        {Icon && <FlexBox className="button-icon mr-sm">{Icon}</FlexBox>}
        <Body variant="body5" className="button-label">
          {children}
        </Body>
      </FlexBox>
    </StyledButton>
  );
};

export default Button;

const StyledButton = styled.button<{
  variant: ButtonVariant;
  color: ButtonColor;
  height?: number;
}>`
  border: 0;
  outline: 0;
  cursor: pointer;
  padding: 0 ${spacing.medium}px;
  height: ${(props) => (props.height ? `${props.height}px` : "38px")};
  align-items: center;
  justify-content: center;
  border-radius: 7px;
  transition: all 0.5s ease;

  ${(props) => `
    ${getDynamicDefaultStyle(props)}

    :hover {
      ${getDynamicHoverStyle(props)}
    }
  `};
`;
